package listening

import AttemptDetail
import ReadingQuestion
import ReadingSet
import UserProgress
import androidx.compose.runtime.*
import io.github.jan.supabase.auth.auth
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.query.Order
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import supabase

@Composable
fun ListeningTask2Screen(
    taskId: Int = 12, // ID для Tarea 2
    onExit: () -> Unit
) {
    // --- STATE ---
    var questions by remember { mutableStateOf<List<ReadingQuestion>>(emptyList()) }
    var currentSet by remember { mutableStateOf<ReadingSet?>(null) }
    var userAnswers by remember { mutableStateOf<Map<String, String>>(emptyMap()) } // Map<QuestionID, OptionLetter>
    var isLoading by remember { mutableStateOf(true) }
    var isFinished by remember { mutableStateOf(false) }
    val startTime = remember { kotlinx.browser.window.performance.now() }
    val scope = rememberCoroutineScope()

    // --- ЗАГРУЗКА ДАННЫХ ---
    LaunchedEffect(taskId) {
        isLoading = true
        try {
            val currentUser = supabase.auth.currentUserOrNull()

            // 1. Ищем нерешенный сет
            val solvedSetIds = if (currentUser != null) {
                supabase.from("user_progress").select {
                    filter { eq("user_id", currentUser.id); eq("task_id", taskId) }
                }.decodeList<UserProgress>().mapNotNull { it.setId }
            } else emptyList()

            val allSets = supabase.from("reading_sets").select {
                filter { eq("task_type", taskId) }
            }.decodeList<ReadingSet>()

            val targetSet = allSets.firstOrNull { it.id !in solvedSetIds }
            currentSet = targetSet

            // 2. Загружаем вопросы
            if (targetSet != null) {
                questions = supabase.from("reading_items").select {
                    filter { eq("set_id", targetSet.id) }
                    order("order_index", Order.ASCENDING)
                }.decodeList<ReadingQuestion>()
            }
        } catch (e: Exception) {
            console.error("Error loading Task 2: ${e.message}")
        } finally {
            isLoading = false
        }
    }

    // --- ЛОГИКА ЗАВЕРШЕНИЯ ---
    fun finishTask() {
        if (currentSet == null) return
        isFinished = true

        val correctCount = questions.count { userAnswers[it.id] == it.correctOption }
        val duration = ((kotlinx.browser.window.performance.now() - startTime) / 1000).toInt()

        // Формируем детализацию
        val details = questions.map { q ->
            AttemptDetail(
                question = q.question,
                userAnswer = userAnswers[q.id] ?: "Sin respuesta",
                correctAnswer = q.correctOption,
                isCorrect = userAnswers[q.id] == q.correctOption
            )
        }

        scope.launch {
            DataRepository.saveProgress(taskId, currentSet!!.id, correctCount, questions.size)
            DataRepository.saveAttempt(
                taskId = taskId,
                setTitle = currentSet!!.title,
                score = correctCount,
                total = questions.size,
                durationSeconds = duration,
                details = details
            )
        }
    }

    // --- UI RENDER ---
    Div({
        style {
            padding(20.px); maxWidth(800.px); property("margin", "0 auto")
            fontFamily("Roboto, sans-serif")
        }
    }) {
        if (isLoading) {
            H3 { Text("⏳ Cargando audio y preguntas...") }
            return@Div
        }

        if (questions.isEmpty()) {
            Div({ style { textAlign("center"); marginTop(50.px) } }) {
                H2 { Text("🎉 ¡Todo completado!") }
                P { Text("No hay más tareas de este tipo disponibles.") }
                Button({ onClick { onExit() }; style { padding(10.px, 20.px); cursor("pointer") } }) { Text("Volver") }
            }
            return@Div
        }

        // Кнопка выхода
        Button({
            onClick { onExit() };
            style { marginBottom(15.px); cursor("pointer") }
        }) { Text("⬅ Salir") }

        // Аудио плеер
        currentSet?.audioUrl?.let { url ->
            Div({
                style {
                    position(Position.Sticky); top(10.px);
                    property("z-index", "100")
                    backgroundColor(Color.white);
                    padding(10.px);
                    borderRadius(
                        10.px
                    ); property("box-shadow", "0 4px 10px rgba(0,0,0,0.1)"); marginBottom(20.px)
                }
            }) {
                H4({ style { margin(0.px); marginBottom(10.px) } }) { Text("🎧 Escuche la entrevista:") }
                Audio({
                    attr("controls", "")
                    style { width(100.percent) }
                }) {
                    Source({ attr("src", url) })
                }
            }
        }

        H2({ style { color(Color("#2c3e50")) } }) { Text(currentSet?.title ?: "Tarea 2") }
        P({ style { color(Color("#7f8c8d")); marginBottom(20.px) } }) { Text("Escuche y seleccione la opción correcta para cada pregunta.") }

        // Список вопросов
        questions.forEachIndexed { index, q ->
            Div({
                style {
                    backgroundColor(Color.white); padding(20.px); marginBottom(20.px); borderRadius(10.px)
                    property("box-shadow", "0 2px 5px rgba(0,0,0,0.05)")
                    border(1.px, LineStyle.Solid, Color("#eee"))
                }
            }) {
                H4({ style { marginTop(0.px); color(Color("#34495e")) } }) {
                    Text("${index + 1}. ${q.question}")
                }

                // Варианты ответов
                val options = listOf("A" to q.optionA, "B" to q.optionB, "C" to q.optionC)

                options.forEach { (letter, text) ->
                    val isSelected = userAnswers[q.id] == letter
                    val isCorrect = q.correctOption == letter

                    // Цвет кнопки
                    val bg = when {
                        isFinished && isCorrect -> Color("#d4edda") // Зеленый (правильный)
                        isFinished && isSelected && !isCorrect -> Color("#f8d7da") // Красный (ошибка)
                        isSelected -> Color("#d6eaf8") // Синий (выбран)
                        else -> Color.white
                    }

                    val borderCol = if (isSelected || (isFinished && isCorrect)) Color("#3498db") else Color("#ecf0f1")

                    Div({
                        onClick { if (!isFinished) userAnswers = userAnswers + (q.id to letter) }
                        style {
                            display(DisplayStyle.Flex); alignItems(AlignItems.Center)
                            padding(12.px); marginBottom(8.px); borderRadius(8.px)
                            border(1.px, LineStyle.Solid, borderCol)
                            backgroundColor(bg)
                            cursor(if (isFinished) "default" else "pointer")
                        }
                    }) {
                        // Кружок с буквой
                        Span({
                            style {
                                fontWeight("bold"); color(Color.white)
                                backgroundColor(
                                    if (isSelected || (isFinished && isCorrect)) Color("#3498db") else Color(
                                        "#bdc3c7"
                                    )
                                )
                                width(25.px); height(25.px); borderRadius(50.percent)
                                display(DisplayStyle.Flex); justifyContent(JustifyContent.Center); alignItems(AlignItems.Center)
                                marginRight(10.px); fontSize(0.8.cssRem)
                            }
                        }) { Text(letter) }

                        Text(text)
                    }
                }
            }
        }

        // Кнопка завершения
        if (!isFinished) {
            Button({
                style {
                    width(100.percent); padding(15.px); backgroundColor(Color("#2c3e50")); color(Color.white)
                    border(0.px); borderRadius(8.px); fontSize(1.2.cssRem); cursor("pointer")
                }
                onClick { finishTask() }
            }) {
                val answered = userAnswers.size
                Text("Finalizar ($answered/${questions.size})")
            }
        } else {
            // Результат
            val score = questions.count { userAnswers[it.id] == it.correctOption }
            Div({
                style {
                    textAlign("center"); padding(20.px); backgroundColor(Color("#f1c40f")); color(Color.white)
                    borderRadius(10.px); marginTop(20.px); fontWeight("bold"); fontSize(1.5.cssRem)
                }
            }) {
                Text("Resultado: $score / ${questions.size}")
            }

            Button({
                style {
                    marginTop(15.px); padding(10.px, 20.px); cursor("pointer"); width(100.percent)
                    backgroundColor(Color.white); border(1.px, LineStyle.Solid, Color("#ccc"))
                }
                onClick { onExit() }
            }) { Text("Volver al menú") }
        }
    }
}